"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import type { Locale, Dictionary } from "./types";
import { en } from "./en";
import { it } from "./it";

const dictionaries: Record<Locale, Dictionary> = { en, it };

interface I18nContextValue {
  locale: Locale;
  t: Dictionary;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nContextValue>({
  locale: "en",
  t: en,
  setLocale: () => {},
});

const STORAGE_KEY = "turtletrack-locale";

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "it") return stored;
  } catch {
    // localStorage unavailable
  }
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("en");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setLocaleState(getInitialLocale());
    setMounted(true);
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const value: I18nContextValue = {
    locale,
    t: dictionaries[locale],
    setLocale,
  };

  // During SSR / hydration, render with default locale to avoid mismatch
  if (!mounted) {
    return (
      <I18nContext.Provider value={{ locale: "en", t: en, setLocale }}>
        {children}
      </I18nContext.Provider>
    );
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}

/**
 * Format a relative time string using the current locale's translations.
 */
export function useFormatRelativeTime() {
  const { t } = useI18n();
  return (date: Date | string | number): string => {
    // Handle both Date objects and ISO strings from API
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const diffMs = Date.now() - dateObj.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);
    if (diffMin < 1) return t.time.now;
    if (diffMin < 60) return `${diffMin} ${t.time.minAgo}`;
    if (diffH < 24) return `${diffH}${t.time.hoursAgo}`;
    return `${Math.floor(diffH / 24)}${t.time.daysAgo}`;
  };
}
